import { readEntitlementFailure, entitlementRequiredCredits } from '@/lib/entitlement-failure';

/** Account preferences are separate root keys: the API shallow-merges them atomically. */
export const CREDITS_JOURNEY_KEYS = {
  // v1 recorded an open request, including cards never painted. Do not treat
  // that historical flag as visibility proof for a still-unclaimed gift.
  shown: 'credits_journey_welcome_visible_v2',
  read: 'credits_journey_welcome_read_v1',
  lowEpisode: 'credits_journey_low_episode_v1',
} as const;

export const CREDITS_WELCOME_TIMEOUT_MS = 12_000;

export interface LowCreditsEpisode {
  projectId: string;
  messageId: string;
  taskId: string;
  recoveryThreshold: number;
  dismissed: boolean;
}

export interface JourneyTaskSnapshot {
  taskId: string;
  messageId: string;
  status: 'active' | 'completed' | 'failed';
  hasMedia?: boolean;
}

export function parseLowCreditsEpisode(value: unknown): LowCreditsEpisode | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.projectId !== 'string' || !record.projectId
    || typeof record.messageId !== 'string' || !record.messageId
    || typeof record.taskId !== 'string' || !record.taskId
    || typeof record.recoveryThreshold !== 'number'
    || !Number.isFinite(record.recoveryThreshold) || record.recoveryThreshold <= 0
  ) return null;
  return {
    projectId: record.projectId,
    messageId: record.messageId,
    taskId: record.taskId,
    recoveryThreshold: record.recoveryThreshold,
    dismissed: record.dismissed === true,
  };
}

/** A range estimate is an upper bound, never a promise of a remaining generation count. */
export function lowCreditsThreshold(quote: number | null): number {
  return quote !== null && Number.isFinite(quote) && quote > 0 ? Math.ceil(quote * 2) : 1;
}

export function isLowCreditsBalance(balance: number, threshold: number): boolean {
  return Number.isFinite(balance) && balance >= 0 && balance < threshold;
}

/** Synchronous UX check only; unavailable quotes/balances defer to authoritative server admission. */
export function knownInsufficientCredits(quote: number | null, balance: number | null, loading: boolean, error: unknown): number | null {
  return !loading && !error && quote !== null && Number.isFinite(quote) && quote > 0
    && balance !== null && Number.isFinite(balance) && balance >= 0 && balance < quote
    ? quote : null;
}

/** Only machine-readable admission codes qualify; never infer billing failures from prose. */
export function parseInsufficientCreditFailure(value: unknown): { requiredCredits?: number } | null {
  const failure = readEntitlementFailure(value);
  if (failure?.code !== 'INSUFFICIENT_CREDITS') return null;
  const requiredCredits = entitlementRequiredCredits(failure);
  return requiredCredits === undefined ? {} : { requiredCredits };
}

/** Only active -> successful transitions qualify; loading old completed history never does. */
export function newlyCompletedJourneyTasks(
  previous: readonly JourneyTaskSnapshot[],
  current: readonly JourneyTaskSnapshot[],
): JourneyTaskSnapshot[] {
  const previousById = new Map(previous.map((task) => [task.taskId, task]));
  return current.filter((task) => (
    task.status === 'completed' && task.hasMedia !== false && previousById.get(task.taskId)?.status === 'active'
  ));
}

/** Render the server instant in the browser's timezone, including the zone to avoid midnight ambiguity. */
export function formatCreditSnapshotAt(value: string | undefined | null, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(date);
}
